import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { routes } from "../routing/routes";
import { FormProvider } from "../components/context/form-provider";
import { ErrorPage } from "../components/complaints/error-page";
import { LanguageSelector } from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";
import { usePraxis } from "../praxis-config/praxis-context";
import { FlowDefinition } from "../flows/types";
import { InfoPage } from "@shared/praxis-config";
import { infoPageRoute } from "./8-info/info-page-routes";

const progressOnGeneralPages: Record<string, number> = {
  [routes.questionnaire.personalInformation]: 5,
  [routes.questionnaire.send]: 99,
};

const FINAL_PAGES_PROGRESS_START = 95;
const FINAL_PAGES_PROGRESS_END = 99;

const createProgressOnFinalPages = (
  infoPages: InfoPage[],
): Record<string, number> => {
  const finalPages = [
    routes.questionnaire.almostDone,
    routes.questionnaire.cancelAppointment,
    routes.questionnaire.additionalPayment,
    ...infoPages.map((page) => infoPageRoute(page.id)),
  ];
  const step =
    (FINAL_PAGES_PROGRESS_END - FINAL_PAGES_PROGRESS_START) /
    (finalPages.length - 1);

  return Object.fromEntries(
    finalPages.map((path, index) => [
      path,
      FINAL_PAGES_PROGRESS_START + index * step,
    ]),
  );
};

const createProgressOnQuestionnairePages = (
  flow: FlowDefinition,
  infoPages: InfoPage[],
): Record<string, number> => ({
  ...progressOnGeneralPages,
  ...createProgressOnFinalPages(infoPages),
  ...Object.fromEntries(
    (flow.pages ?? []).map((page) => [page.path, page.progress]),
  ),
  ...Object.fromEntries(
    flow.questionnaires.flatMap((questionnaireData) =>
      Object.entries(questionnaireData).map(([path, page]) => [
        path,
        page.progress,
      ]),
    ),
  ),
});

const useProgressOnQuestionnairePages = () => {
  const { flow, config } = usePraxis();
  return useMemo(
    () => createProgressOnQuestionnairePages(flow, config.infoPages),
    [flow, config.infoPages],
  );
};

const ProgressBar: React.FC = () => {
  const route = useLocation();
  const progressOnQuestionnairePages = useProgressOnQuestionnairePages();

  const progress = progressOnQuestionnairePages[route.pathname];
  if (progress === undefined) {
    console.error("Invalid route pathname", route.pathname);
  }

  return (
    <div className="w-full bg-gray-50 rounded-full h-2.5 overflow-hidden border border-gray-400">
      <div
        className="bg-primary h-full transition-all duration-300 ease-in-out"
        style={{ width: `${progress ?? 0}%` }}
      ></div>
    </div>
  );
};

export default function IndexPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname !== routes.questionnaire.base) {
      window.location.replace("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <ResponsiveBackground>
      <ResponsiveContainer>
        <FormProvider>
          <Outlet />
        </FormProvider>
      </ResponsiveContainer>
    </ResponsiveBackground>
  );
}

const ResponsiveBackground: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center bg-desktop">
      {children}
    </div>
  );
};

const ResponsiveContainer: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const route = useLocation();
  const { t } = useTranslation();

  // To make the progress bar with nice animations, it has to be in the Outlet wrapper
  // The back button is part of the Outlet wrapper too because the progress bar is below the back button
  const isBaseOrDoneRoute =
    route.pathname === routes.questionnaire.base ||
    route.pathname === routes.questionnaire.done;

  const progressOnQuestionnairePages = useProgressOnQuestionnairePages();
  const shouldShowProgressBarAndBackButton =
    !isBaseOrDoneRoute && route.pathname in progressOnQuestionnairePages;

  const navigateBack = () => {
    navigate(-1);
  };

  return (
    <div className="relative w-full grow sm:grow-0 sm:max-w-screen-sm sm:rounded-2xl bg-mobile sm:bg-none sm:bg-white sm:shadow-responsive-card px-6 pt-12 pb-3 sm:my-2 flex flex-col justify-between">
      <div>
        <ErrorBoundary
          FallbackComponent={ErrorPage}
          onError={(error) => {
            Sentry.captureException(error);
          }}
        >
          <LanguageSelector className="absolute right-4 top-2 md:top-4" />
          {shouldShowProgressBarAndBackButton && (
            <div className="flex flex-col gap-10">
              <div
                className="flex flex-row gap-2 items-center text-sm cursor-pointer text-gray-500 self-start"
                onClick={navigateBack}
              >
                <ArrowLeftIcon width={15} />
                <span>{t("common.back")}</span>
              </div>
              <ProgressBar />
            </div>
          )}
          {children}
        </ErrorBoundary>
      </div>
      <div className="text-sm text-gray-500 flex flex-row justify-end gap-3 mt-10">
        <Link target="_blank" to={routes.imprint}>
          {t("common.imprint")}
        </Link>
        <Link target="_blank" to={routes.privacy}>
          {t("common.privacy")}
        </Link>
      </div>
    </div>
  );
};
